import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import i18n from '../../i18n/i18n';

type DataPoint = {
    time: number;
    power: number;
};

interface PowerChartProps {
    data: DataPoint[];
    theme: any;
    variant?: string;
}

const PowerChart = ({ data, theme, variant }: PowerChartProps) => {
  const rounToInt = (num: number) => Math.round(num);
  // Calculate maximum power
  const maxPower = Math.max(...data.map((item) => item.power));
  // Calculate average power
  const averagePower = data.reduce((sum, item) => sum + item.power, 0) / data.length;

  // Galp (Figma 519-2973): thick brand-orange curve, no area fill, dashed horizontal
  // gridlines only, round Y ticks (0,20,…) with a "kW" unit reference, integer time ticks.
  if (variant === 'galp') {
    const lineColor = theme.graphicLineColor || theme.graphicColor || '#FF5A00';
    const axisColor = '#616061';
    return (
      <div style={{ position: 'relative', width: '100%', fontFamily: theme.bodyFont }}>
        <span style={{
          position: 'absolute', top: 0, left: 4, fontSize: 11, color: axisColor,
        }}
        >
          kW
        </span>
        <ResponsiveContainer width="100%" height={270}>
          <AreaChart
            data={data}
            margin={{
              top: 20, right: 14, left: 6, bottom: 26,
            }}
          >
            <CartesianGrid stroke="#dedede" strokeDasharray="4 4" vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              domain={['dataMin', 'dataMax']}
              ticks={data.length <= 15 ? data.map((d) => d.time) : undefined}
              tickLine={false}
              axisLine={{ stroke: '#111110' }}
              tick={{ fill: axisColor, fontSize: 11, fontFamily: theme.bodyFont }}
              label={{
                value: `${i18n.t('time')} (min)`,
                position: 'insideBottom',
                offset: -6,
                fill: axisColor,
                fontSize: 11,
              }}
            />
            <YAxis
              domain={[0, 'auto']}
              tickCount={7}
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              width={40}
              tick={{ fill: axisColor, fontSize: 11, fontFamily: theme.bodyFont }}
            />
            <Area
              type="monotone"
              dataKey="power"
              stroke={lineColor}
              strokeWidth={2.5}
              fill="none"
              fillOpacity={0}
              dot={false}
              activeDot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart
        width={500}
        height={400}
        data={data}
        margin={{
          top: 20,
          right: 20,
          left: 1,
          bottom: 20,
        }}
      >
        <defs>
          <linearGradient id="colorChart" x1="0" y1="0" x2="0" y2="1">
            <stop offset="70%" stopColor={theme.graphicGradientEnd || theme.graphicColor} stopOpacity={0.8} />
            <stop offset="95%" stopColor={theme.graphicGradientStart || theme.graphicColor} stopOpacity={0.5} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#f5f5f5" />
        <XAxis
          dataKey="time"
          type="number"
          domain={[0, 'dataMax']}
          tick={{ fill: theme.textColor || '#6B7280', fontSize: 12, fontWeight: 'bold' }}
          label={{
            value: `${i18n.t('time')} (min)`,
            position: 'insideBottom',
            offset: -5,
            fontSize: 12,
          }}
        />
        <YAxis
          tick={{ fill: theme.textColor || '#6B7280', fontSize: 12, fontWeight: 'bold' }}
          tickFormatter={(value) => `${rounToInt(value)} kW`}
          orientation="left"
          mirror
          dx={-5}
          dy={-10}
          tickMargin={5}
          ticks={[0, averagePower, maxPower]}
        />

        <Tooltip
          contentStyle={{
            backgroundColor: theme.cardBackground || '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          labelStyle={{ color: theme.textColor }}
          formatter={(value) => [`${rounToInt(value as number)} kW`, 'Power']}
        />

        {/* Average Power */}
        {/* <ReferenceLine y={averagePower} stroke="#8884d8" strokeDasharray="3 3" /> */}

        <Area
          type="monotone"
          dataKey="power"
          stroke={theme.titleColor}
          fillOpacity={1}
          fill="url(#colorChart)"
          style={{ opacity: 0.75 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default PowerChart;
